import React, { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Panel, Text, Header, Modal, Button, Actions } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import Recaptcha from '../Recaptcha';
import StarRating from '../StarRating';
import SubmitReview from '../SubmitReview';
import ReviewItem from '../ReviewItem';
import { localyticsEvent, noop } from '../../../lib/utils';
import { ANALYTICS_EVENTS } from '../../constants';
import styles from './ReviewPanel.module.css';

const cx = classnames.bind(styles);

export default function ReviewPanel({
  reviews,
  rating,
  stationId,
  updateReview,
}) {
  const recaptchaInstance = useRef();
  const [hasRecaptchaLoaded, setHasRecaptchaLoaded] = useState(false);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const [recaptchaResponse, setRecaptchaResponse] = useState(undefined);
  const [reviewBeingFlagged, setReviewBeingFlagged] = useState();
  const [wasUpdatingReview, setWasUpdatingReview] = useState(false);
  const [shouldShowThankYou, setShouldShowThankYou] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [hasInitialized, setHasInitialized] = useState(false);

  const onFlagReview = useCallback((reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (isRecaptchaVerified && recaptchaInstance.current) {
      // Reset the recaptcha instance
      recaptchaInstance.current.reset();
      setIsRecaptchaVerified(false);
    }
    setReviewBeingFlagged(review);
    setErrorMessage(undefined);
  }, [reviews, isRecaptchaVerified, recaptchaInstance]);

  const handleCancelFlagReview = useCallback(() => {
    setReviewBeingFlagged(undefined);
    setWasUpdatingReview(false);
    setShouldShowThankYou(false);
    setErrorMessage(undefined);
  }, []);

  const handleRecaptchaLoaded = useCallback(() => {
    setHasRecaptchaLoaded(true);
  }, []);

  const handleRecaptchaVerified = useCallback((response) => {
    setRecaptchaResponse(response);
    setIsRecaptchaVerified(true);
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptchaResponse(null);
    setIsRecaptchaVerified(false);
  }, []);

  const handleFlagReview = useCallback(() => {
    if (reviewBeingFlagged?.id && hasRecaptchaLoaded && isRecaptchaVerified) {
      setErrorMessage(undefined);
      setWasUpdatingReview(true);
      updateReview({
        id: reviewBeingFlagged.id,
        hasFlagged: true,
        recaptchaResponse,
      });
    }
  }, [updateReview, reviewBeingFlagged, hasRecaptchaLoaded, isRecaptchaVerified, recaptchaResponse]);

  // When flagging is done, show thank you or error depending on response
  useEffect(() => {
    if (reviewBeingFlagged) {
      if (wasUpdatingReview && !reviewBeingFlagged.isUpdating && !reviewBeingFlagged.updateError) {
        setShouldShowThankYou(true);
        localyticsEvent(ANALYTICS_EVENTS.REVIEW_FLAGGED);
      } else if (reviewBeingFlagged.updateError) {
        setErrorMessage(reviewBeingFlagged.updateError);
      }
    }
  }, [reviewBeingFlagged, wasUpdatingReview]);

  useEffect(() => {
    setHasInitialized(true);
  }, []);

  return (
    <React.Fragment>
      <Modal
        size="sm"
        content={() => (
          shouldShowThankYou
            ? (
              <React.Fragment>
                <Header as="h3">Thanks for your feedback</Header>
                <Text as="p">
                  The review has been flagged as inappropriate. Someone will follow up soon. Thanks for your help!
                </Text>
                <br />
                <Button primary wide onClick={handleCancelFlagReview}>Done</Button>
              </React.Fragment>
            )
            : hasInitialized && (
              <React.Fragment>
                <Header as="h3">Are you sure you want to flag this station review as being inappropriate?</Header>
                <Text as="p">
                  Inappropriate reviews can include, but are not limited to swearing, unnecessary insults etc.
                </Text>
                <Text as="p">
                  Once you flag a review, you will be unable to agree with it.
                </Text>
                <Text as="p">
                  Before flagging this review, we need to know that you&rsquo;re not a robot <span role="img" aria-labelledby="recaptcha">🤖</span>
                </Text>
                <br />

                <Recaptcha
                  ref={recaptchaInstance}
                  elementID="recaptcha"
                  className={cx('recaptchaContainer')}
                  render="explicit"
                  verifyCallback={handleRecaptchaVerified}
                  onloadCallback={handleRecaptchaLoaded}
                  expiredCallback={handleRecaptchaExpired}
                />
                <br />
                {!!errorMessage && (
                  <Text as="p" color="orange">
                    {errorMessage}
                  </Text>
                )}
                <Actions>
                  <Actions.Button
                    primary
                    fluid
                    onClick={handleFlagReview}
                    loading={reviewBeingFlagged?.isUpdating}
                    disabled={reviewBeingFlagged?.isUpdating}
                    desktop={4}
                  >
                    Yes, I&rsquo;m sure
                  </Actions.Button>
                  <Actions.Button
                    secondary
                    fluid
                    onClick={handleCancelFlagReview}
                    desktop={4}
                  >
                    Cancel
                  </Actions.Button>
                </Actions>
              </React.Fragment>
            )
        )}
        forceIsShowing={!!reviewBeingFlagged}
        onClose={handleCancelFlagReview}
      />
      <Panel color="white" padded={false} bordered rounded>
        <FlexGrid className={styles.reviewsRatingHeader}>
          <FlexGrid.Column mobile={6} tablet={2} className={cx('rightBorder')}>
            <Text bold className={cx('averageRating')} color="midnight">
              {rating.toFixed(1)}
            </Text>
            <br />
            <Text size="sm" color="midnight">Overall Rating</Text>
          </FlexGrid.Column>
          <FlexGrid.Column mobile={6} tablet={4} className={cx('starRatingContainer')}>
            <StarRating name="stationReviewsRating" rating={rating} size="small" />
          </FlexGrid.Column>
          <FlexGrid.Column tablet={6} className={cx('writeReviewContainer')}>
            {hasInitialized && (
              <SubmitReview stationId={stationId} />
            )}
          </FlexGrid.Column>
        </FlexGrid>
      </Panel>
      {reviews.map(review => (
        <ReviewItem
          key={review.id}
          review={review}
          showReplies
          onFlagReview={onFlagReview}
          interactive
        />
      ))}
      <small className={cx('footnote')}>
        These reviews are the subjective opinion of a GasBuddy member and not of GasBuddy LLC
      </small>
    </React.Fragment>
  );
}

ReviewPanel.propTypes = {
  stationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  reviews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    date: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    user: PropTypes.shape({
      avatarUrl: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  })).isRequired,
  rating: PropTypes.number,
  updateReview: PropTypes.func,
};

ReviewPanel.defaultProps = {
  rating: 0,
  updateReview: noop,
};
